/**
 * OpeningHoursEditor — weekly schedule editor (Mon→Sun).
 *
 * Value shape: { monday: { open: "08:00", close: "17:00", closed: false }, ... }
 *   - closed=true means the venue isn't open that day at all (open/close ignored).
 *   - undefined/null value also renders fine (treated as "no schedule yet").
 *
 * Includes a "Copy from previous day" button on every row for quick fill-in,
 * plus a "Copy Monday to weekdays" shortcut.
 */
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Copy } from "lucide-react";

const DAYS = [
  { key: "monday",    label: "Mon" },
  { key: "tuesday",   label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday",  label: "Thu" },
  { key: "friday",    label: "Fri" },
  { key: "saturday",  label: "Sat" },
  { key: "sunday",    label: "Sun" },
];

const DEFAULT_DAY = { open: "08:00", close: "17:00", closed: false };

function dayValue(value, key) {
  return value?.[key] || { ...DEFAULT_DAY };
}

export default function OpeningHoursEditor({ value, onChange }) {
  const v = value || {};

  const updateDay = (key, patch) => {
    const next = { ...v, [key]: { ...dayValue(v, key), ...patch } };
    onChange?.(next);
  };

  const copyFromPrevious = (idx) => {
    if (idx === 0) return;
    const prevKey = DAYS[idx - 1].key;
    const curKey = DAYS[idx].key;
    onChange?.({ ...v, [curKey]: { ...dayValue(v, prevKey) } });
  };

  const copyMondayToWeekdays = () => {
    const mon = dayValue(v, "monday");
    const next = { ...v };
    ["tuesday", "wednesday", "thursday", "friday"].forEach((k) => { next[k] = { ...mon }; });
    onChange?.(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Opening hours
        </Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={copyMondayToWeekdays}
          data-testid="opening-hours-copy-mon-to-weekdays"
        >
          <Copy className="w-3 h-3 mr-1" />
          Copy Mon → Tue–Fri
        </Button>
      </div>

      <div className="space-y-1.5 rounded-md border border-neutral-200 p-2 bg-neutral-50/50">
        {DAYS.map((d, idx) => {
          const dv = dayValue(v, d.key);
          return (
            <div key={d.key} className="flex items-center gap-2" data-testid={`opening-hours-row-${d.key}`}>
              <span className="w-10 text-sm font-medium text-neutral-700">{d.label}</span>

              <Switch
                checked={!dv.closed}
                onCheckedChange={(open) => updateDay(d.key, { closed: !open })}
                data-testid={`opening-hours-open-${d.key}`}
              />

              {dv.closed ? (
                <span className="text-xs text-muted-foreground italic flex-1">Closed</span>
              ) : (
                <>
                  <Input
                    type="time"
                    value={dv.open || "08:00"}
                    onChange={(e) => updateDay(d.key, { open: e.target.value })}
                    className="h-8 text-sm w-28"
                    data-testid={`opening-hours-open-time-${d.key}`}
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={dv.close || "17:00"}
                    onChange={(e) => updateDay(d.key, { close: e.target.value })}
                    className="h-8 text-sm w-28"
                    data-testid={`opening-hours-close-time-${d.key}`}
                  />
                </>
              )}

              {idx > 0 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 ml-auto"
                  title="Copy from previous day"
                  onClick={() => copyFromPrevious(idx)}
                  data-testid={`opening-hours-copy-prev-${d.key}`}
                >
                  <Copy className="w-3.5 h-3.5 text-neutral-500" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
